import type { ReactNode } from 'react';
import { AlertTriangle, Check, ChevronRight, X } from 'lucide-react';
import { cn } from './cn';
import type { Tone } from './StatusBadge';

/**
 * The top of a detail page, after the name: the few numbers that describe
 * the record, and the short list of what is right and what is not.
 *
 * Every admin record — a company, a partner, an enquiry — was opening on a
 * grid of equally weighted boxes: address, contact, registration, setup.
 * A reader had to scan all four to learn the one thing they came for, which
 * is usually "is this account in good standing, and if not, why". These
 * three primitives put that first, in the same shape on every record, so
 * the answer is in the same place whichever screen somebody lands on.
 */

/* ── Key figures ────────────────────────────────────────────── */

export interface Figure {
  key: string;
  label: string;
  value: ReactNode;
  /** One quieter line under the number: "stated at registration", "3 inactive". */
  hint?: ReactNode;
  /**
   * Colours the number. Only for a figure whose meaning IS its state — an
   * outstanding balance is not a neutral fact — so four figures do not become
   * four colours.
   */
  tone?: Extract<Tone, 'covered' | 'dueSoon' | 'overdue' | 'pending'>;
  onClick?: () => void;
  loading?: boolean;
}

const FIGURE_TEXT: Record<NonNullable<Figure['tone']>, string> = {
  covered: 'text-covered-600', dueSoon: 'text-dueSoon-600', overdue: 'text-overdue-600', pending: 'text-pending-600',
};

/**
 * A row of compact numbers: `[ 2 plants ] [ 1,240 workers ] [ ₹42,000 owed ]`.
 *
 * Smaller than a StatCard on purpose. A dashboard tile is the point of its
 * screen; these sit under a name and are read once before the tabs.
 */
export function KeyFigures({ figures, label = 'At a glance', className }: {
  figures: Array<Figure | false | null | undefined>;
  label?: string;
  className?: string;
}) {
  const shown = figures.filter(Boolean) as Figure[];
  if (!shown.length) return null;
  return (
    // Five or six figures in four columns leave one or two alone on a second
    // row, reading as a different kind of thing from the four above them.
    <ul
      aria-label={label}
      className={cn(
        // Four tiles go two by two on a portrait tablet: three across left
        // the fourth alone on a row of its own.
        'grid grid-cols-2 gap-2', shown.length === 4 ? 'sm:grid-cols-2' : 'sm:grid-cols-3',
        // A phone gets one line that scrolls sideways: six tiles in two
        // columns took a third of the screen before the list they summarise.
        // The third tile is cut by the edge on purpose — it says there is more.
        // Written as `max-md` overrides, not as a new base: callers pass
        // their own `sm:`/`lg:` column counts, and those must go on winning
        // from `md` up exactly as they did.
        'max-md:flex max-md:snap-x max-md:overflow-x-auto max-md:pb-1',
        // One row only where each tile still has room for its label: beside
        // the sidebar a laptop gives six tiles 140px each, and "Examined —
        // not cleared" was cut to "Examined — no…".
        shown.length === 3 ? 'lg:grid-cols-3' : shown.length === 5 ? 'xl:grid-cols-5' : shown.length === 6 ? 'xl:grid-cols-6' : 'lg:grid-cols-4',
        className,
      )}
    >
      {shown.map((f) => {
        const Wrapper = f.onClick ? 'button' : 'div';
        return (
          <li key={f.key} className="min-w-0 max-md:w-[9.5rem] max-md:shrink-0 max-md:snap-start">
            <Wrapper
              {...(f.onClick ? { type: 'button' as const, onClick: f.onClick } : {})}
              className={cn(
                // Full height: a hint that wraps on one tile left its neighbours shorter.
                'block h-full w-full rounded-card border border-neutral-200 bg-white px-3 py-2.5 text-left shadow-card',
                f.onClick && 'transition-colors duration-hover hover:border-neutral-300 hover:bg-neutral-50',
              )}
            >
              <div className="truncate text-caption text-neutral-500">{f.label}</div>
              <div className={cn('mt-0.5 truncate text-h3 tabular-nums', f.tone ? FIGURE_TEXT[f.tone] : 'text-neutral-900')}>
                {f.loading
                  ? <span className="inline-block h-6 w-14 animate-pulse rounded bg-neutral-100 align-middle" />
                  : f.value}
              </div>
              {f.hint && <div className="line-clamp-2 text-caption font-normal text-neutral-500">{f.hint}</div>}
            </Wrapper>
          </li>
        );
      })}
    </ul>
  );
}

/* ── Checklist ──────────────────────────────────────────────── */

/**
 * `done` is a quiet check. `todo` is something still to do, without alarm.
 * `warning` needs attention but does not stop anything. `blocked` stops the
 * next act — an approval, an empanelment — and is drawn to say so.
 */
export type CheckState = 'done' | 'todo' | 'warning' | 'blocked';

export interface CheckItem {
  key: string;
  label: ReactNode;
  state: CheckState;
  /** One short line: what is missing, or what to do about it. */
  detail?: ReactNode;
  /** A link or a small button, beside the item. */
  action?: ReactNode;
}

function Mark({ state }: { state: CheckState }) {
  const cls = 'flex h-5 w-5 shrink-0 items-center justify-center rounded-full';
  switch (state) {
    case 'done':
      return <span aria-hidden className={cn(cls, 'bg-success-100 text-success-600')}><Check className="h-3.5 w-3.5" strokeWidth={3} /></span>;
    case 'warning':
      return <span aria-hidden className={cn(cls, 'bg-warning-100 text-warning-600')}><AlertTriangle className="h-3 w-3" strokeWidth={2.5} /></span>;
    case 'blocked':
      return <span aria-hidden className={cn(cls, 'bg-danger-100 text-danger-600')}><X className="h-3.5 w-3.5" strokeWidth={3} /></span>;
    default:
      return <span aria-hidden className={cn(cls, 'border-2 border-neutral-300 bg-white')} />;
  }
}

const STATE_WORD: Record<CheckState, string> = {
  done: 'done', todo: 'not yet', warning: 'needs attention', blocked: 'blocking',
};

/**
 * What is in order and what is not, as a list a reader can take in at once.
 *
 * Each line carries its state as a mark AND as words for a screen reader,
 * and colour never carries it alone.
 */
export function Checklist({ items, className }: {
  items: Array<CheckItem | false | null | undefined>;
  className?: string;
}) {
  const shown = items.filter(Boolean) as CheckItem[];
  return (
    <ul className={cn('space-y-2', className)}>
      {shown.map((it) => (
        <li key={it.key} className="flex items-start gap-2.5">
          <span className="mt-0.5"><Mark state={it.state} /></span>
          <div className="min-w-0 flex-1">
            <div className={cn(
              'text-body',
              it.state === 'blocked' ? 'font-medium text-danger-600'
                : it.state === 'warning' ? 'font-medium text-neutral-900'
                  : it.state === 'done' ? 'text-neutral-800' : 'text-neutral-600',
            )}>
              {it.label}
              <span className="sr-only"> — {STATE_WORD[it.state]}</span>
            </div>
            {it.detail && (
              <div className={cn('text-caption font-normal', it.state === 'blocked' ? 'text-danger-600' : 'text-neutral-500')}>
                {it.detail}
              </div>
            )}
          </div>
          {it.action && <div className="shrink-0">{it.action}</div>}
        </li>
      ))}
    </ul>
  );
}

/**
 * The checklist in its panel: a heading, the items, and the acts they lead to.
 *
 * The panel's edge follows the worst item on it, so a page with one blocking
 * item says so before a single line is read.
 */
export function AttentionPanel({ title, items, summary, actions, className }: {
  title: ReactNode;
  items: Array<CheckItem | false | null | undefined>;
  /** One line under the list: "Complete 1 item before approval." */
  summary?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  const shown = items.filter(Boolean) as CheckItem[];
  const worst: CheckState = shown.some((i) => i.state === 'blocked') ? 'blocked'
    : shown.some((i) => i.state === 'warning') ? 'warning'
      : shown.some((i) => i.state === 'todo') ? 'todo' : 'done';
  return (
    <section
      aria-label={typeof title === 'string' ? title : undefined}
      className={cn(
        'rounded-card border bg-white p-4 shadow-card',
        worst === 'blocked' ? 'border-danger-600/40' : worst === 'warning' ? 'border-warning-600/40' : 'border-neutral-200',
        className,
      )}
    >
      <h2 className="text-caption font-semibold uppercase tracking-wider text-neutral-700">{title}</h2>
      <Checklist items={shown} className="mt-3" />
      {(summary || actions) && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-neutral-100 pt-3">
          <p className={cn('text-small', worst === 'blocked' ? 'font-medium text-danger-600' : 'text-neutral-600')}>
            {summary}
          </p>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
    </section>
  );
}

/* ── Action notice ──────────────────────────────────────────── */

/**
 * "3 registrations awaiting approval →", above a list.
 *
 * One line, one act. A list page that opens on the thing that needs doing
 * is a list page that gets read; the count is the whole message and the
 * row is the button.
 */
export function ActionNotice({ tone = 'dueSoon', children, hint, onClick, className }: {
  tone?: Extract<Tone, 'dueSoon' | 'overdue' | 'pending' | 'info'>;
  children: ReactNode;
  hint?: ReactNode;
  onClick: () => void;
  className?: string;
}) {
  const TONE: Record<NonNullable<typeof tone>, string> = {
    dueSoon: 'border-dueSoon-600/30 bg-dueSoon-100 text-dueSoon-600 hover:bg-dueSoon-100/70',
    overdue: 'border-overdue-600/30 bg-overdue-100 text-overdue-600 hover:bg-overdue-100/70',
    pending: 'border-pending-600/30 bg-pending-100 text-pending-600 hover:bg-pending-100/70',
    info: 'border-info-600/30 bg-info-100 text-info-600 hover:bg-info-100/70',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center justify-between gap-3 rounded-card border px-4 py-3 text-left transition-colors duration-hover',
        TONE[tone], className,
      )}
    >
      <span className="min-w-0">
        <span className="block text-caption font-medium uppercase tracking-wide opacity-80">Needs attention</span>
        <span className="block text-body font-semibold">{children}</span>
        {hint && <span className="block text-small font-normal opacity-90">{hint}</span>}
      </span>
      <ChevronRight aria-hidden className="h-5 w-5 shrink-0" />
    </button>
  );
}
